import { useEffect, useRef } from 'react';
import { useUIStore } from '@/stores';
import './RulesModal.css';

/**
 * RulesModal - 遊び方（ルール説明）モーダル
 * ネオン・ノワール調。CLAUDE.md / GameRules に準拠した独自ルールを正確に提示する。
 * SettingsModal のモーダルパターンに倣い、uiStore で開閉を管理する。
 */
const RulesModal = () => {
  const isOpen = useUIStore((state) => state.modals.rules.isOpen);
  const closeRules = useUIStore((state) => state.closeRules);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  // ESC キーで閉じる
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeRules();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeRules]);

  // 開いたら閉じるボタンへフォーカス
  useEffect(() => {
    if (isOpen) {
      closeBtnRef.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="RulesModal__overlay" onClick={closeRules}>
      <div
        className="RulesModal__content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="遊び方"
      >
        {/* ヘッダー */}
        <header className="RulesModal__header">
          <h2 className="RulesModal__title">遊び方</h2>
          <button
            ref={closeBtnRef}
            className="RulesModal__close"
            onClick={closeRules}
            aria-label="閉じる"
          >
            ×
          </button>
        </header>

        {/* ボディ（縦スクロール） */}
        <div className="RulesModal__body">
          {/* 1. 概要 / 2 vs 1 */}
          <section className="RulesModal__section">
            <h3 className="RulesModal__section-title">概要 — 2 vs 1</h3>
            <p className="RulesModal__lead">
              <span className="RulesModal__cop">警察チーム</span>（警官2人のデュオ）が、
              <span className="RulesModal__crook">犯人</span>（1人のソロ）を追い詰める頭脳戦。
            </p>
            <ul className="RulesModal__list">
              <li>
                <span className="RulesModal__cop">警察チーム（Team 1）</span>
                ＝警官2人。<span className="RulesModal__crook">犯人（Team 2）</span>＝1人。
              </li>
              <li>
                基本のターン順は <strong>警察 → 犯人 → 警察 → 犯人 …</strong> と進む。
              </li>
              <li>
                <strong>同じチームのプレイヤーは手札を共有</strong>する（味方の手札が見える）。
              </li>
            </ul>
          </section>

          {/* 2. 持ち点と勝敗 */}
          <section className="RulesModal__section">
            <h3 className="RulesModal__section-title">持ち点（ライフ）と勝敗</h3>
            <div className="RulesModal__life-grid">
              <div className="RulesModal__life-card RulesModal__life-card--cop">
                <span className="RulesModal__life-team">警察（Team 1）</span>
                <span className="RulesModal__life-value">各 60</span>
                <span className="RulesModal__life-unit">POINTS</span>
              </div>
              <div className="RulesModal__life-card RulesModal__life-card--crook">
                <span className="RulesModal__life-team">犯人（Team 2）</span>
                <span className="RulesModal__life-value">80</span>
                <span className="RulesModal__life-unit">POINTS</span>
              </div>
            </div>
            <p className="RulesModal__note">
              いずれかのプレイヤーの持ち点が <strong>0 以下</strong> になった時点で試合終了。
              相手チームの勝利となる。試合は複数ラウンド制で進行する。
            </p>
          </section>

          {/* 3. カードと出し方 */}
          <section className="RulesModal__section">
            <h3 className="RulesModal__section-title">カードと出し方</h3>
            <ul className="RulesModal__list">
              <li>
                色は <span className="RulesModal__chip RulesModal__chip--red">赤</span>
                <span className="RulesModal__chip RulesModal__chip--blue">青</span>
                <span className="RulesModal__chip RulesModal__chip--yellow">黄</span> の3色。
              </li>
              <li>
                種類は 数字（0〜9）、スキップ、リバース、ドロー2、ワイルド、ワイルドドロー4。
              </li>
              <li>
                場の最上段カードと <strong>同じ色</strong>、または{' '}
                <strong>同じ数字 / 種類</strong> なら出せる。
              </li>
              <li>
                ワイルドはいつでも出せる。出したら次に続く色を宣言する。
              </li>
            </ul>
          </section>

          {/* 4. ダメージ */}
          <section className="RulesModal__section">
            <h3 className="RulesModal__section-title">ダメージ</h3>
            <p className="RulesModal__note">
              ラウンドに敗北したチームは、<strong>残った手札</strong>からダメージを計算して受ける。
            </p>
            <table className="RulesModal__table">
              <thead>
                <tr>
                  <th>カード</th>
                  <th>ダメージ</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>数字（0〜9）</td>
                  <td className="RulesModal__dmg">額面の値</td>
                </tr>
                <tr>
                  <td>スキップ</td>
                  <td className="RulesModal__dmg">20</td>
                </tr>
                <tr>
                  <td>リバース</td>
                  <td className="RulesModal__dmg">20</td>
                </tr>
                <tr>
                  <td>ドロー2</td>
                  <td className="RulesModal__dmg">20</td>
                </tr>
                <tr>
                  <td>ワイルド</td>
                  <td className="RulesModal__dmg">40</td>
                </tr>
                <tr>
                  <td>ワイルドドロー4</td>
                  <td className="RulesModal__dmg RulesModal__dmg--max">50</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* 5. ラウンドの流れ */}
          <section className="RulesModal__section">
            <h3 className="RulesModal__section-title">ラウンドの流れ</h3>
            <ol className="RulesModal__steps">
              <li>各プレイヤーに7枚のカードを配る。</li>
              <li>
                どちらかのチームが手札を <strong>0枚</strong> にしたら、そのラウンドの勝利。
              </li>
              <li>
                敗北チームの残り手札からダメージを計算し、持ち点を減らす。
              </li>
              <li>
                持ち点が 0 以下になれば試合終了。そうでなければ次のラウンドへ進む。
              </li>
            </ol>
          </section>

          {/* 6. UNO宣言 / ペナルティ */}
          <section className="RulesModal__section RulesModal__section--last">
            <h3 className="RulesModal__section-title">UNO宣言とペナルティ</h3>
            <ul className="RulesModal__list">
              <li>
                手札が残り <strong>2枚</strong> になったら「UNO」を宣言できる（専用ボタンが出る）。
              </li>
              <li>
                ドロー2 / ドロー4 を出された次のプレイヤーは、回避できない場合は{' '}
                <strong>自動でペナルティ枚数を引く</strong>。
              </li>
              <li>
                設定でスタッキング（重ね出し）を有効にできる。
              </li>
            </ul>
          </section>
        </div>

        {/* フッター */}
        <footer className="RulesModal__footer">
          <button
            className="RulesModal__button RulesModal__button--primary"
            onClick={closeRules}
          >
            閉じる
          </button>
        </footer>
      </div>
    </div>
  );
};

export default RulesModal;
